/**
 * BackButton — the ONE shared back control (pill). Every app's sub-page back
 * uses this so the affordance looks and behaves identically everywhere; restyle
 * it in BackButton.css to change all of them at once.
 *
 * "Back" is `navigate(-1)`: it pops to the exact previous history entry,
 * restoring that page's full URL — so any filters / pagination the origin keeps
 * in its query string come back for free. Completion flows that would otherwise
 * leave a dead entry behind (e.g. a finished solving session) already navigate
 * with `{ replace: true }`, so the pop lands on the real origin. `fallback` is
 * used only when there is no history to pop (deep link / refresh); it should
 * itself carry query params so even that path lands with the right filters.
 *
 * Label resolution: an incoming `returnTo` (the cross-page convention — see
 * navigation/returnTo.ts) wins, else the `label` prop. Either way it renders
 * "‹ Back to <label>"; with neither, a chevron-only square.
 *
 * `compact` drops the "Back to" prefix and renders "‹ <label>" — for headers
 * where the control shares a row with other chrome (the in-session header sits
 * beside the title, due date and question progress). The ACCESSIBLE name stays
 * "Back to <label>" either way, so shortening the visual label never costs a
 * screen-reader user the destination.
 */
import { useNavigate } from 'react-router-dom';
import { useReturnLocation } from '../navigation/returnTo';
import { hasHistoryBehind } from '../navigation/hasHistoryBehind';
import './BackButton.css';

export interface BackButtonProps {
    /** Route used only when there is no history to pop. May carry query params. */
    fallback?: string;
    /** Fixed destination for deliberate flow *exits* (e.g. a post-results page
     *  whose back must go Home, not pop into the finished session). When set,
     *  always navigates here instead of popping history. Reserve for genuine
     *  exits — ordinary sub-page backs omit it and use navigate(-1). */
    to?: string;
    /** Fully custom click handler — overrides `to` and the navigate(-1)/fallback
     *  default. Use only when the back needs logic a URL can't express (branching
     *  or router-state passing). */
    onClick?: () => void;
    /** Static label ("‹ Back to <label>"); chevron-only when omitted. An incoming
     *  `returnTo.label` takes precedence when present. */
    label?: string;
    /** Drop the "Back to" prefix — renders "‹ <label>". For space-constrained
     *  headers only; the aria-label is unaffected. */
    compact?: boolean;
    className?: string;
}

export function BackButton({ fallback = '/home', to, onClick, label, compact, className = '' }: BackButtonProps) {
    const navigate = useNavigate();
    const returnTo = useReturnLocation();
    const shownLabel = returnTo?.label ?? label;
    const handleClick = () => {
        // An incoming returnTo means "pop back to the page that linked here" —
        // it wins over a page's own default back (onClick/to/fallback).
        if (returnTo) navigate(-1);
        else if (onClick) onClick();
        else if (to) navigate(to);
        else if (hasHistoryBehind()) navigate(-1);
        else navigate(fallback);
    };
    return (
        <button
            type="button"
            className={`back-btn ${shownLabel ? 'back-btn--labelled' : ''} ${shownLabel && compact ? 'back-btn--compact' : ''} ${className}`.trim()}
            aria-label={shownLabel ? `Back to ${shownLabel}` : 'Back'}
            onClick={handleClick}
        >
            <span className="back-btn-chevron" aria-hidden="true">‹</span>
            {shownLabel && <span className="back-btn-label">{compact ? shownLabel : `Back to ${shownLabel}`}</span>}
        </button>
    );
}
